import React, { createContext, ReactNode, useContext } from "react";
import { useQuery, useMutation, UseMutationResult } from "@tanstack/react-query";
import { z } from "zod";
import { queryClient } from "../lib/queryClient";
import { toast } from "../hooks/use-toast";

// Import schemas
// We're defining these here temporarily to avoid import issues, will refactor later
const _loginSchema = z.object({
  email: z.string().email("Please enter a valid email"),
  password: z.string().min(1, "Password is required"),
});

const _registerSchema = z
  .object({
    email: z.string().email("Please enter a valid email"),
    firstName: z.string().min(1, "First name is required"),
    lastName: z.string().min(1, "Last name is required"),
    password: z.string().min(8, "Password must be at least 8 characters"),
    confirmPassword: z.string(),
    setupToken: z.string().optional(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

// User type
interface User {
  id: string;
  companyId: number | null;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: "user" | "supervisor" | "manager" | "admin" | "owner";
  isSuperAdmin?: boolean;
  platformAccess?: "none" | "saas_marketing" | "saas_staff" | "super_admin";
  profileImageUrl: string | null;
  supervisorId: string | null;
  isVerified: boolean;
  phoneNumber: string | null;
  notificationsOptIn: boolean | null;
  workingGenius: string | null;
  isReferralPartner?: boolean;
  requiresLogin?: boolean;
}

type LoginCredentials = z.infer<typeof _loginSchema>;
type RegisterData = z.infer<typeof _registerSchema>;

type AuthContextType = {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: Error | null;
  loginMutation: UseMutationResult<User, Error, LoginCredentials>;
  logoutMutation: UseMutationResult<void, Error, void>;
  registerMutation: UseMutationResult<User, Error, RegisterData>;
};

const AuthContext = createContext<AuthContextType | null>(null);

// API request helper
async function apiRequest(method: string, endpoint: string, data?: any) {
  const res = await fetch(endpoint, {
    method,
    headers: {
      "Content-Type": "application/json",
    },
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include", // Include cookies for session authentication
  });
  return res;
}

// Query function helper
function getQueryFn({ on401 }: { on401: "returnNull" | "throw" }) {
  return async ({ queryKey }: any) => {
    const endpoint = queryKey[0];
    const res = await fetch(endpoint, {
      credentials: "include", // Include cookies for session authentication
    });

    if (res.status === 401) {
      if (on401 === "returnNull") {
        return null;
      }
      throw new Error("Unauthorized");
    }

    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.message || "Something went wrong");
    }

    return res.json();
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const {
    data: user,
    error,
    isLoading,
  } = useQuery<User | null, Error>({
    queryKey: ["/api/auth/user"],
    queryFn: getQueryFn({ on401: "returnNull" }),
  });

  const loginMutation = useMutation({
    mutationFn: async (credentials: LoginCredentials) => {
      const res = await apiRequest("POST", "/api/login", credentials);
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || "Failed to log in");
      }
      return await res.json();
    },
    onSuccess: async (user: User) => {
      // Invalidate and refetch to get the full user data (including phoneNumber, notificationsOptIn, workingGenius)
      await queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      await queryClient.refetchQueries({ queryKey: ["/api/auth/user"] });
      toast({
        title: "Welcome back!",
        description: `You've successfully logged in as ${user.firstName || user.email}`,
      });

      // Let React Router handle the redirect based on authentication state
      // The DashboardHome component will redirect to the appropriate page based on role
      window.location.href = "/";
    },
    onError: (error: Error) => {
      toast({
        title: "Login failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const registerMutation = useMutation({
    mutationFn: async (registerData: RegisterData) => {
      try {
        console.log("Sending registration data:", registerData);
        const res = await fetch("/api/register", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(registerData),
          credentials: "include",
        });

        console.log("Registration response status:", res.status);
        console.log("Registration response ok:", res.ok);

        if (!res.ok) {
          let errorMsg = "Failed to register";
          try {
            const errorData = await res.json();
            console.log("Error response data:", errorData);
            errorMsg = errorData.message || errorMsg;
          } catch (e) {
            console.log("Failed to parse error response as JSON:", e);
            errorMsg = res.statusText || errorMsg;
          }
          throw new Error(errorMsg);
        }

        const userData = await res.json();
        console.log("Registration successful, user data:", userData);
        return userData;
      } catch (error) {
        console.log("Registration error caught:", error);
        if (error instanceof Error) {
          throw error;
        }
        throw new Error("Registration failed. Please try again.");
      }
    },
    onSuccess: async (user: User) => {
      if (user.requiresLogin) {
        toast({
          title: "Account setup complete",
          description: "Log in to confirm your two-factor authentication code.",
        });
        window.location.href = `/auth?email=${encodeURIComponent(user.email)}`;
        return;
      }

      // Invalidate and refetch to get the full user data
      await queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      await queryClient.refetchQueries({ queryKey: ["/api/auth/user"] });
      toast({
        title: "Registration successful!",
        description: "Your account has been created successfully.",
      });

      // SaaS-only invitees (no company) land in the SaaS dashboard.
      const platformAccess = (user as any).platformAccess as string | undefined;
      const hasSaasAccess =
        user.isSuperAdmin ||
        platformAccess === "saas_marketing" ||
        platformAccess === "saas_staff" ||
        platformAccess === "super_admin";
      if (hasSaasAccess && !user.companyId) {
        window.location.href = "/all-organizations";
      } else if (
        user.role === "admin" ||
        user.role === "owner" ||
        user.role === "manager" ||
        user.role === "supervisor" ||
        user.isSuperAdmin
      ) {
        window.location.href = "/team-reports";
      } else {
        window.location.href = "/my-reports";
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Registration failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/logout");
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || "Failed to log out");
      }
    },
    onSuccess: () => {
      queryClient.setQueryData(["/api/auth/user"], null);
      toast({
        title: "Logged out",
        description: "You've been successfully logged out.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Logout failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider
      value={{
        user: user ?? null,
        isLoading,
        isAuthenticated,
        error,
        loginMutation,
        logoutMutation,
        registerMutation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
